import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { ASSETS } from "@/lib/services";

export default function Header() {
  const { t, availableLanguages, lang } = useContent();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onAdmin = location.pathname.startsWith("/admin");

  if (onAdmin) return null;

  const nav = [
    { to: "/", label: t("nav.home", "Home") },
    { to: "/about", label: t("nav.about", "About") },
    { to: "/services", label: t("nav.services", "Services") },
    { to: "/contact", label: t("nav.contact", "Contact") },
  ];

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md bg-bone-50/85 border-b border-ink-800/10"
      data-testid="site-header"
    >
      <div className="section flex items-center justify-between py-4">
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
          <img
            src={ASSETS.logo}
            alt="VF"
            className="h-11 w-11 object-contain bg-ink-800 p-1.5"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-[15px] text-ink-800 tracking-wide">
              Vasileios Filippidis
            </span>
            <span className="uppercase text-[10px] tracking-[0.28em] text-gold-600">
              Law Office
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `relative uppercase tracking-[0.22em] text-[11px] py-2 transition-colors ${
                  isActive ? "text-gold-600" : "text-ink-800 hover:text-gold-600"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="hidden md:flex items-center gap-1 border border-ink-800/15 px-2 py-1.5"
            data-testid="language-switcher"
          >
            {availableLanguages.map((l) => (
              <button
                key={l}
                disabled
                title={l === lang ? "Current language" : "Coming soon"}
                className={`uppercase text-[10px] tracking-[0.2em] px-1.5 ${
                  l === lang ? "text-gold-600" : "text-ink-800/40"
                }`}
                data-testid={`lang-btn-${l}`}
              >
                {l}
              </button>
            ))}
          </div>

          <Link to="/contact" className="btn-gold hidden md:inline-flex" data-testid="header-cta">
            {t("nav.cta", "Book a Consultation")}
          </Link>

          <button
            className="lg:hidden p-2 text-ink-800"
            aria-label="Open menu"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            <i className={open ? "fa-solid fa-xmark text-xl" : "fa-solid fa-bars text-xl"} />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-ink-800/10 bg-bone-50" data-testid="mobile-menu">
          <div className="section py-6 flex flex-col gap-5">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `uppercase tracking-[0.22em] text-xs ${
                    isActive ? "text-gold-600" : "text-ink-800"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="btn-gold w-fit"
              data-testid="mobile-header-cta"
            >
              {t("nav.cta", "Book a Consultation")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
